import type { ReactNode } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";

/**
 * Campos e rodapé das etapas da cotação.
 *
 * Existem porque os três estágios usavam `InputWithLabel` e `CustomButton`,
 * que trazem o visual antigo (`bg-background`, que fora do escopo do admin sai
 * transparente, e botões cheios de 48px). Ficam aqui, e não em components/, por
 * serem do desenho desta tela.
 */

export const Campo = ({
  label,
  valor,
  aoMudar,
  placeholder,
  sufixo,
  tipo = "text",
  invalido,
}: {
  label: string;
  valor: string;
  aoMudar: (v: string) => void;
  placeholder?: string;
  sufixo?: string;
  tipo?: "text" | "number";
  invalido?: boolean;
}) => (
  <label className="block min-w-0">
    <span className="mb-1.5 block text-[11px] font-medium text-slate-500">{label}</span>
    <div className="relative">
      <input
        type={tipo === "number" ? "text" : "text"}
        inputMode={tipo === "number" ? "decimal" : undefined}
        value={valor}
        onChange={(e) => aoMudar(e.target.value)}
        placeholder={placeholder}
        className={`h-10 w-full rounded-lg border bg-white px-3 text-sm text-slate-700 outline-none transition-colors placeholder:text-slate-300 focus:ring-1 ${
          invalido
            ? "border-red-300 focus:border-red-400 focus:ring-red-300"
            : "border-slate-200 focus:border-brand-primary focus:ring-brand-primary"
        } ${sufixo ? "pr-10" : ""}`}
      />
      {sufixo && (
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">
          {sufixo}
        </span>
      )}
    </div>
  </label>
);

export const Selecao = ({
  label,
  valor,
  aoMudar,
  opcoes,
  placeholder = "Selecione",
}: {
  label: string;
  valor: string;
  aoMudar: (v: string) => void;
  opcoes: { value: string; label: string }[];
  placeholder?: string;
}) => (
  <label className="block min-w-0">
    <span className="mb-1.5 block text-[11px] font-medium text-slate-500">{label}</span>
    <select
      value={valor}
      onChange={(e) => aoMudar(e.target.value)}
      className="h-10 w-full cursor-pointer rounded-lg border border-slate-200 bg-white px-2.5 text-sm text-slate-700 outline-none transition-colors focus:border-brand-primary focus:ring-1 focus:ring-brand-primary"
    >
      <option value="">{placeholder}</option>
      {opcoes.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  </label>
);

export const Secao = ({
  titulo,
  extra,
  children,
}: {
  titulo: string;
  extra?: ReactNode;
  children: ReactNode;
}) => (
  <section className="space-y-2.5">
    <div className="flex items-baseline justify-between gap-3">
      <h3 className="text-sm font-semibold text-slate-800">{titulo}</h3>
      {extra}
    </div>
    {children}
  </section>
);

/**
 * Barra de ações no pé da etapa.
 *
 * O par antigo era um grid de dois botões cheios do mesmo tamanho, com o
 * "Cancelar" em vermelho pleno — a ação destrutiva chamava mais atenção que a
 * de avançar. Aqui cancelar é texto, a dica diz o que falta e o avanço é o
 * único botão sólido.
 */
export const RodapeEtapa = ({
  onCancelar,
  onVoltar,
  onAvancar,
  rotuloAvancar,
  avancarLiberado,
  dica,
}: {
  onCancelar?: () => void;
  onVoltar?: () => void;
  onAvancar: () => void;
  rotuloAvancar: string;
  avancarLiberado: boolean;
  dica?: string;
}) => (
  <div className="mt-6 flex items-center justify-between gap-4 border-t border-slate-100 pt-4">
    {onCancelar ? (
      <button
        type="button"
        onClick={onCancelar}
        className="text-sm text-slate-500 transition-colors hover:text-slate-700"
      >
        Cancelar
      </button>
    ) : (
      <span />
    )}

    <div className="flex items-center gap-3">
      {dica && <span className="text-right text-xs text-slate-400">{dica}</span>}

      {onVoltar && (
        <button
          type="button"
          onClick={onVoltar}
          className="flex h-10 items-center gap-1.5 rounded-lg border border-slate-200 px-3 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Voltar
        </button>
      )}

      <button
        type="button"
        onClick={onAvancar}
        disabled={!avancarLiberado}
        className="flex h-10 items-center gap-2 rounded-lg bg-brand-sidebar px-4 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400"
      >
        {rotuloAvancar}
        <ArrowRight className="h-4 w-4" />
      </button>
    </div>
  </div>
);
